<template>
    <div class="item-availability-indicator">
        <!-- visual status dot (green, yellow, red, grey) -->
        <span class="dot" :class="dotClasses"></span>
        <!-- is store offline -->
        <template v-if="store.isShopOnline === false">
            <div>
                {{ $t("shop.No status information") }}
            </div>
        </template>
        <!-- is store online -->
        <template v-else>
            <!-- no stock information or incomplete status information for other shops -->
            <div>
                {{ $t("shop.{stockDisplayText} in stock", { stockDisplayText: stockDisplayText }) }}
            </div>
        </template>
    </div>
</template>
<script setup lang="ts">
import { ShopCore } from "@/abilities/shop/types/shop-core";
import { getUiLanguage } from "@/shared/services/providers/language-provider";
import { computed } from "vue";
import { useShopStatusStore } from "../store/shop-status.store";

const props = defineProps<{
    infoItem: ShopCore.WidgetItem | null;
}>();

const store = useShopStatusStore();

const stockDisplayText = computed(() => {
    //for shop light, we do not have quantity information, so it is set to -1. For FDI/Salesforce, if shopitem is null, there is a fallback on 0
    if (props.infoItem?.stock.quantity !== -1) {
        return (
            props.infoItem?.stock.quantity.toLocaleString(getUiLanguage(), {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }) ?? 0
        );
    }
    return "";
});

const dotClasses = computed(() => {
    // for all other shops (fdi, salesforce, ...)
    return {
        available: props.infoItem?.stock.available === ShopCore.StockStatus.AVAILABLE,
        "low-stock": props.infoItem?.stock.available === ShopCore.StockStatus.LOW_STOCK,
        "out-of-stock": props.infoItem?.stock.available === ShopCore.StockStatus.OUT_OF_STOCK,
        unavailable:
            store.isShopOnline === false ||
            props.infoItem === null ||
            props.infoItem?.stock.available === ShopCore.StockStatus.UNAVAILABLE,
    };
});
</script>
<style lang="scss">
.item-availability-indicator {
    display: flex;
    align-items: baseline;

    .dot {
        height: 10px;
        width: 10px;
        border-radius: 50%;
        display: block;
        margin-right: $spacing-m;
    }

    $dot-size: 10px;
    $dot-colors: (
        available: var(--q-positive),
        low-stock: var(--q-warning),
        out-of-stock: var(--q-negative),
        unavailable: var(--q-negative),
    );

    @each $status, $color in $dot-colors {
        .dot.#{$status} {
            background-color: $color;
        }
    }
}
</style>
